/* global React, Icon, Card, Button, Modal, EmptyState, fmt, t */
// ============================================================
// AI Rådgiver — Skatt: valgkort og skatteforslag
// ============================================================
// De fire tallene per forslag vises hver for seg og summeres aldri. Er et tall ikke
// relevant for forslaget, tegnes ruten ikke — den står ikke tom med en strek.
const { useState: taxUseState } = React;

const AII_TAX_FIG_TONE = {
  reviewAmount: 'var(--ink)',
  taxBaseReduction: 'var(--ink)',
  taxSaving: 'var(--success)',
  netCost: 'var(--ink)',
};

const AII_TAX_DEDUCTION_CHECKLIST = [
  {
    id: 'operations',
    titleKey: 'adv.tax.checklist.operations',
    title: 'Drift og administrasjon',
    items: [
      'Kontorrekvisita, programvare, abonnementer og fagsystemer som brukes i virksomheten.',
      'Regnskap, juridisk bistand, bankgebyrer, betalingsløsninger og andre profesjonelle tjenester.',
      'Telefon, internett og hjemmekontorandel når kostnaden kan dokumenteres og fordeles saklig.',
    ],
  },
  {
    id: 'sales',
    titleKey: 'adv.tax.checklist.sales',
    title: 'Salg, marked og kunder',
    items: [
      'Annonser, nettside, foto, grafisk arbeid, domene, trykksaker og annet salgsarbeid.',
      'Kurs, konferanser, medlemskap og faglig oppdatering som har tydelig sammenheng med inntekten.',
      'Representasjon innenfor fradragsreglene, med deltaker, formål og bilag på plass.',
    ],
  },
  {
    id: 'travel',
    titleKey: 'adv.tax.checklist.travel',
    title: 'Reise og bil',
    items: [
      'Yrkesreise, parkering, bom, kollektiv, fly, hotell og diett etter gjeldende satser.',
      'Kjørebok for næringskjøring med dato, formål, fra/til og kilometer.',
      'Varebil, leasing, forsikring, drivstoff og vedlikehold der bilen faktisk brukes i virksomheten.',
    ],
  },
  {
    id: 'assets',
    titleKey: 'adv.tax.checklist.assets',
    title: 'Utstyr og lokaler',
    items: [
      'PC, mobil, verktøy, inventar og andre driftsmidler. Store kjøp kan måtte aktiveres og avskrives.',
      'Leie av lokale, verksted, lager, coworking eller andel av lokaler brukt i virksomheten.',
      'Reparasjon, vedlikehold, renhold, strøm og andre lokalkostnader som hører til driften.',
    ],
  },
  {
    id: 'finance',
    titleKey: 'adv.tax.checklist.finance',
    title: 'Finans og tap',
    items: [
      'Renter, purregebyrer og finansieringskostnader knyttet til virksomheten.',
      'Tap på kundefordringer når kravet er reelt forsøkt inndrevet og tapet er dokumentert.',
      'Valutatap, kortgebyrer og provisjoner som gjelder salg eller kjøp i virksomheten.',
    ],
  },
  {
    id: 'people',
    titleKey: 'adv.tax.checklist.people',
    title: 'Ansatte og hjelpere',
    items: [
      'Lønn, arbeidsgiveravgift, OTP, forsikringer og personaltiltak innenfor reglene.',
      'Honorar til frilansere, underleverandører og innleid hjelp med riktig dokumentasjon.',
      'Arbeidsklær og verneutstyr når det er yrkesmessig behov, ikke vanlig privat bekledning.',
    ],
  },
];

function AiiTaxDeductionChecklistModal({ onClose }) {
  return <Modal open onClose={onClose} title={t('adv.tax.checklist.title', 'Huskeliste for fradrag')} max={820}
    footer={<Button onClick={onClose}>{t('common.close', 'Lukk')}</Button>}>
    <div className="aii-tax-checklist-modal">
      <div className="aii-tax-checklist-note">
        <Icon name="info" size={17} />
        <p>{t('adv.tax.checklist.note', 'Dette er en praktisk kontrolliste for vanlige fradrag små selskaper ofte overser. Fradrag krever alltid dokumentasjon, riktig formål og vurdering mot gjeldende regler.')}</p>
      </div>
      <div className="aii-tax-checklist-grid">
        {AII_TAX_DEDUCTION_CHECKLIST.map((group) => <section key={group.id} className="aii-tax-checklist-group">
          <h4>{t(group.titleKey, group.title)}</h4>
          <ul>
            {group.items.map((item, index) => <li key={item}>{t(`adv.tax.checklist.${group.id}.${index + 1}`, item)}</li>)}
          </ul>
        </section>)}
      </div>
      <p className="t-xs muted" style={{ margin: 0 }}>{t('adv.tax.checklist.footer', 'Tips: Bruk listen sammen med bilagene dine før MVA, skattemelding og årsavslutning. Usikre poster bør avklares med regnskapsfører eller Skatteetatens veiledning.')}</p>
    </div>
  </Modal>;
}

// ---------- Decision summary ----------
// The figures and assumptions live in the proposal section below.
function AiiTaxChoices({ choices }) {
  const [showAll, setShowAll] = taxUseState(false);
  if (!choices?.length) return null;
  return <section className="aii-section-frame">
    <window.AiSecHead icon="sparkle" title={t('adv.tax.choices.title')} sub={t('adv.tax.choices.sub')} help={t('adv.tax.choices.help')} />
    <div className="aii-tsc-grid" style={{ marginTop: 14 }}>
      {(showAll ? choices : choices.slice(0, 3)).map((choice) => {
        return <div key={choice.id} className={'aii-tsc tone-' + (choice.recommendation === 'wait' ? 'amber' : choice.recommendation === 'do' ? 'green' : 'plum')}>
        <div className="aii-tsc-head">
          <span className="aii-tsc-ico"><Icon name={choice.icon} size={19} /></span>
          <span className="aii-tsc-q">{t('adv.tax.choice.' + choice.id + '.q')}</span>
          {choice.recommendation === 'wait' && <span className="aii-tax-wait"><Icon name="clock" size={12} />{t('adv.tax.wait')}</span>}
        </div>
        <p className="aii-tsc-reco">
          <Icon name="sparkle" size={15} />
          <span>{t('adv.tax.choice.' + choice.id + '.summary.' + (choice.recommendation || 'consider'))}</span>
        </p>
        <button type="button" className="aii-tax-insight-link" onClick={() => document.getElementById('aii-tax-proposal-' + choice.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
          {t('adv.tax.choices.details')} <Icon name="chevRight" size={14} />
        </button>
      </div>; })}
    </div>
    {choices.length > 3 && <button type="button" className="aii-tax-insight-link aii-tsc-show-more" onClick={() => setShowAll((value) => !value)}>{t(showAll ? 'adv.tax.choices.showLess' : 'adv.tax.choices.showMore', { count: choices.length - 3 })} <Icon name={showAll ? 'chevUp' : 'chevDown'} size={14} /></button>}
  </section>;
}

// Beløp formatteres av UI, prosent og antall står som de er.
const aiiTaxVars = (vars) => {
  const out = {};
  Object.entries(vars || {}).forEach(([key, value]) => {
    out[key] = /^(amount|vat|saving|reduction|limit|cost)$/.test(key) && Number.isFinite(Number(value)) ? fmt(Number(value)) : value;
  });
  return out;
};

function AiiTaxInsights({ insights, onPurchase, onPlan, onTaxReturn }) {
  const [voucherPage, setVoucherPage] = taxUseState(null);
  if (!insights) return null;
  const { vouchers = [], investments = [], advanceTax = {}, assetEstimateSupported = false } = insights;
  const hasPostings = vouchers.some((item) => item.kind === 'posting');
  const materialGap = advanceTax.gap != null && advanceTax.gap >= 5000
    && advanceTax.gap >= (advanceTax.expected || 0) * 0.1;
  const pageSize = 20;
  const totalPages = Math.ceil(vouchers.length / pageSize);
  const voucherRow = (item) => <div className="aii-tax-insight-row" key={item.id}>
    <span><b>{item.label || item.date || t('adv.tax.insights.purchase')}</b></span>
    <button type="button" onClick={() => { setVoucherPage(null); onPurchase?.(item); }}>{fmt(item.amountNet)} <Icon name="chevRight" size={14} /></button>
  </div>;
  return <React.Fragment><section className="aii-tax-insights aii-section-frame">
    <window.AiSecHead icon="checkCircle" title={t('adv.tax.insights.title')} sub={t('adv.tax.insights.sub')} />
    <div className="aii-tax-insight-grid">
      <Card className="aii-tax-insight">
        <h3>{t('adv.tax.insights.vouchers')}</h3>
        <p>{vouchers.length ? t(hasPostings ? vouchers.length === 1 ? 'adv.tax.insights.postingFoundOne' : 'adv.tax.insights.postingsFound' : 'adv.tax.insights.vouchersFound', { count: vouchers.length }) : t('adv.tax.insights.vouchersNone')}</p>
        {vouchers.slice(0, 3).map(voucherRow)}
        {vouchers.length > 3 && <button type="button" className="aii-tax-insight-link" onClick={() => setVoucherPage(0)}>{t('adv.tax.insights.showAllVouchers', { count: vouchers.length })} <Icon name="chevRight" size={14} /></button>}
      </Card>
      <Card className={'aii-tax-insight' + (materialGap ? ' attention' : '')}>
        <h3>{t('adv.tax.insights.advance')}</h3>
        {advanceTax.gap == null
          ? <p>{t('adv.tax.insights.advanceUnknown')}</p>
          : <React.Fragment>
            <div className="aii-tax-insight-row"><span>{t('adv.tax.insights.expected')}</span><b>{fmt(advanceTax.expected)}</b></div>
            <div className="aii-tax-insight-row"><span>{t('adv.tax.insights.paid')}</span><b>{fmt(advanceTax.paid)}</b></div>
            <div className="aii-tax-insight-row"><span>{t('adv.tax.insights.gap')}</span><b>{fmt(advanceTax.gap)}</b></div>
            <p>{t(materialGap ? 'adv.tax.insights.gapAction' : 'adv.tax.insights.gapNote')}</p>
          </React.Fragment>}
        <p className="t-xs muted">{t('adv.tax.insights.basis.' + advanceTax.basis)}</p>
        <button type="button" className="aii-tax-insight-link" onClick={onTaxReturn}>{t('adv.tax.insights.openTaxReturn')} <Icon name="chevRight" size={14} /></button>
      </Card>
      <Card className="aii-tax-insight">
        <h3>{t('adv.tax.insights.investments')}</h3>
        <p>{investments.length ? t('adv.tax.insights.investmentsFound', { count: investments.length }) : t('adv.tax.insights.investmentsNone')}</p>
        {investments.length > 0 && <p>{t(!assetEstimateSupported ? 'adv.tax.insights.taxTreatmentUnsupported' : investments.every((item) => item.taxDetailsReady) ? 'adv.tax.insights.taxTreatmentReady' : 'adv.tax.insights.taxTreatmentUnknown')}</p>}
        {investments.map((item) => <div className="aii-tax-insight-investment" key={item.id}>
          <div className="aii-tax-insight-row"><b>{item.label || t('adv.tax.insights.investment')}</b><b>{fmt(item.amount)}</b></div>
          <small>{item.date}</small>
          <small>{item.purpose ? t('adv.tax.insights.purpose', { purpose: item.purpose }) : t('adv.tax.insights.purposeMissing')}</small>
          {assetEstimateSupported && !item.taxDetailsReady && <small>{t('adv.tax.insights.taxDetailsMissing')}</small>}
        </div>)}
        <button type="button" className="aii-tax-insight-link" onClick={onPlan}>{t('adv.tax.insights.openPlan')} <Icon name="chevRight" size={14} /></button>
      </Card>
    </div>
  </section>
  {voucherPage != null && <Modal open onClose={() => setVoucherPage(null)} title={t('adv.tax.insights.vouchers')} max={680}>
    <div className="aii-tax-voucher-list">
      {vouchers.slice(voucherPage * pageSize, (voucherPage + 1) * pageSize).map(voucherRow)}
    </div>
    <div className="aii-tax-voucher-pager">
      <Button variant="secondary" size="sm" disabled={voucherPage === 0} onClick={() => setVoucherPage((page) => page - 1)}>{t('adv.tax.insights.previous')}</Button>
      <span>{t('adv.tax.insights.page', { page: voucherPage + 1, total: totalPages })}</span>
      <Button variant="secondary" size="sm" disabled={voucherPage + 1 >= totalPages} onClick={() => setVoucherPage((page) => page + 1)}>{t('adv.tax.insights.next')}</Button>
    </div>
  </Modal>}
  </React.Fragment>;
}

// ---------- Skatteforslag ----------
function AiiTaxProposal({ proposal, onPlan }) {
  const [open, setOpen] = taxUseState(false);
  const figures = [
    proposal.reviewAmount != null ? { key: 'reviewAmount', value: proposal.reviewAmount } : null,
    proposal.taxBaseReduction != null ? { key: 'taxBaseReduction', value: proposal.taxBaseReduction } : null,
    proposal.taxSaving != null ? { key: 'taxSaving', value: proposal.taxSaving } : null,
    proposal.liquidityEffect ? { key: 'liquidityEffect', value: proposal.liquidityEffect } : null,
    proposal.netCost != null ? { key: 'netCost', value: proposal.netCost } : null,
  ].filter(Boolean);
  return <div className="aii-tax" id={'aii-tax-proposal-' + proposal.id}>
    <div className="aii-tax-top">
      <span className="aii-tax-ico"><Icon name={proposal.icon} size={18} /></span>
      <span className="aii-tax-name">{t('adv.tax.proposal.' + proposal.id + '.title')}</span>
    </div>
    <div className="aii-tax-desc">{t('adv.tax.proposal.' + proposal.id + (proposal.reviewReason === 'unsupportedScope' ? '.unsupportedDesc' : proposal.reviewRequired ? '.reviewDesc' : '.desc'), aiiTaxVars(proposal.vars))}</div>
    {proposal.reviewReason === 'missingFacts' && <button type="button" className="aii-tax-insight-link" onClick={onPlan}>{t('adv.tax.insights.openPlan')} <Icon name="chevRight" size={14} /></button>}
    <div className="aii-tax-figs">
      {figures.map((figure) => <div key={figure.key} className="aii-tax-fig">
        <div className="lbl" data-help-title={t('adv.tax.fig.' + figure.key)} data-help={t('adv.help.taxFig.' + figure.key)}>{t('adv.tax.fig.' + figure.key)}</div>
        <div className="val" style={{
          color: figure.key === 'liquidityEffect'
            ? (figure.value < 0 ? 'var(--error)' : 'var(--success)')
            : AII_TAX_FIG_TONE[figure.key],
        }}>
          {figure.key === 'liquidityEffect' && figure.value > 0 ? '+' : figure.value < 0 ? '−' : ''}{fmt(Math.abs(figure.value))}
        </div>
      </div>)}
    </div>
    <div className="aii-tax-actions">
      <button type="button" className="aii-prio-why-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Icon name="sparkle" size={13} />{t('adv.tax.showCalculation')}<Icon name={open ? 'chevUp' : 'chevDown'} size={14} />
      </button>
      {proposal.recommendation === 'wait' && <div className="aii-tax-wait" data-help-title={t('adv.tax.recommendWait')} data-help={t('adv.help.recommendWait')}><Icon name="clock" size={13} />{t('adv.tax.recommendWait')}</div>}
    </div>
    {open && <ul className="aii-prio-why">
      {proposal.calculation.map((line, i) => <li key={i}>{t('adv.tax.calc.' + line.key, aiiTaxVars(line.vars))}</li>)}
    </ul>}
  </div>;
}

function AiiTaxProposals({ advice, onPlan }) {
  return <React.Fragment><Card>
    <window.AiSecHead icon="percent" title={t('adv.tax.title')} sub={t(advice?.proposals?.length ? 'adv.tax.proposalsSub' : 'adv.tax.sub')} help={t('adv.tax.help')}
    />
    {!advice?.proposals?.length ? <EmptyState noVisual acc="percent" title={t('adv.tax.emptyTitle')} text={t('adv.tax.emptyText')} compact /> : <React.Fragment>
    <div className="aii-tax-grid" style={{ marginTop: 14 }}>
      {advice.proposals.map((proposal) => <AiiTaxProposal key={proposal.id} proposal={proposal} onPlan={onPlan} />)}
    </div>
    {/* Satsen står synlig. Et anslag uten oppgitt sats kan ikke etterprøves. */}
    {advice.proposals.some((proposal) => proposal.taxSaving != null) && <p className="t-xs muted" style={{ marginTop: 12 }}>
      {t('adv.tax.rateNote.' + advice.taxRate.basis, { pct: Math.round(advice.taxRate.rate * 1000) / 10 })}
    </p>}
    <div className="aii-disclaimer" data-help-title={t('adv.tax.disclaimerTitle')} data-help={t('adv.tax.disclaimerHelp')}>
      <Icon name="info" size={17} />
      <p>{t('adv.tax.disclaimer')}</p>
    </div>
    {window.PlummeBackend?.getAiAdvisorProfile?.()?.accountantInvolved === true && <p className="t-xs muted" style={{ marginTop: 8 }}>
      <b>{t('adv.profile.discussWithAccountant')}</b> — {t('adv.profile.accountantNote')}
    </p>}
    </React.Fragment>}
  </Card>
  </React.Fragment>;
}

Object.assign(window, { AiiTaxChoices, AiiTaxInsights, AiiTaxProposals, AiiTaxDeductionChecklistModal });
